import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/api';
import { JobOpening } from '../types/api';
import PublicHeader from '../components/PublicHeader';
import PublicFooter from '../components/PublicFooter';
import DocumentTitle from '../components/DocumentTitle';
import MetaDescription from '../components/MetaDescription';
import RichTextDisplay from '../components/RichTextDisplay';
import { useSiteSettings } from '../hooks/useSiteSettings';

const PublicHomePage: React.FC = () => {
  const [homePage, setHomePage] = useState<any>(null);
  const [jobs, setJobs] = useState<JobOpening[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { siteName, siteTagline, loading: settingsLoading } = useSiteSettings();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [homePageData, jobsData] = await Promise.all([
          apiService.getPageBySlug('home'),
          apiService.getActiveJobs()
        ]);
        
        setHomePage(homePageData);
        setJobs(jobsData);
        setError(null);
      } catch (err) {
        setError('Failed to load homepage content');
        console.error('Error fetching homepage data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <PublicHeader />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
        <PublicFooter />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <PublicHeader />
        <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900">Error</h1>
            <p className="mt-4 text-gray-600">{error}</p>
          </div>
        </div>
        <PublicFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <DocumentTitle title="Home" />
      <MetaDescription description={homePage?.meta_description} />
      <PublicHeader />

      {/* Main Content */}
      <main>
        {/* Hero Section */}
        <div className="bg-blue-600">
          <div className="max-w-7xl mx-auto py-16 px-4 sm:py-24 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl md:text-6xl">
                {settingsLoading ? 'Loading...' : `Welcome to ${siteName}`}
              </h1>
              <p className="mt-6 max-w-2xl mx-auto text-xl text-blue-100">
                {settingsLoading ? 'Loading...' : siteTagline}
              </p>
              <div className="mt-10">
                <Link
                  to="/jobs"
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-blue-600 bg-white hover:bg-gray-50"
                >
                  View Open Positions
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        {homePage && (
          <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
            <RichTextDisplay 
              content={homePage.content}
              className="mx-auto"
            />
          </div>
        )}

        {/* Featured Jobs */}
        {jobs.length > 0 && (
          <div className="bg-gray-50">
            <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
              <div className="text-center">
                <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
                  Featured Job Openings
                </h2>
                <p className="mt-4 text-lg text-gray-600">
                  Join our team and be part of something great
                </p>
              </div>
              <div className="mt-12 grid gap-8 lg:grid-cols-3">
                {jobs.map((job) => (
                  <div key={job.id} className="bg-white rounded-lg shadow-md p-6">
                    <h3 className="text-xl font-semibold text-gray-900">{job.title}</h3>
                    <div className="mt-2 text-gray-600 line-clamp-3">
                      <RichTextDisplay content={job.description} />
                    </div>
                    {job.location && (
                      <p className="mt-2 text-sm text-gray-500">📍 {job.location}</p>
                    )}
                    <div className="mt-4">
                      <Link
                        to={`/jobs/${job.id}/apply`}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                      >
                        Apply Now
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8 text-center">
                <Link
                  to="/jobs"
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-blue-600 bg-white hover:bg-gray-50 border-blue-600"
                >
                  View All Jobs
                </Link>
              </div>
            </div>
          </div>
        )}
      </main>

      <PublicFooter />
    </div>
  );
};

export default PublicHomePage;
